import { DropResult } from '@hello-pangea/dnd';
import { OutgoingEventProductDto, OutgoingSectionDto } from 'api/dtos/events/products';
import { SECTION_TYPES } from '../constants';

interface HandleDragEndParams {
  result: DropResult;
  sections: OutgoingSectionDto[] | undefined;
  tiers: OutgoingEventProductDto[] | undefined;
  onReorder?: (
    tiers: OutgoingEventProductDto[],
    sections: OutgoingSectionDto[]
  ) => void;
}

export const handleDragEnd = ({
  result,
  sections,
  tiers,
  onReorder
}: HandleDragEndParams) => {
  if (!result.destination || !onReorder) {
    return;
  }

  const currentSections = Array.from(sections || []);
  const currentTiers = Array.from(tiers || []).sort(
    (a, b) => a.sortIndex - b.sortIndex
  );

  if (result.type === SECTION_TYPES.SECTIONS) {
    handleSectionReorder(result, currentSections, currentTiers, onReorder);
  } else if (result.type === SECTION_TYPES.TIERS) {
    handleTierReorder(result, currentSections, currentTiers, onReorder);
  }
};

const handleSectionReorder = (
  result: DropResult,
  currentSections: OutgoingSectionDto[],
  currentTiers: OutgoingEventProductDto[],
  onReorder: (
    tiers: OutgoingEventProductDto[],
    sections: OutgoingSectionDto[]
  ) => void
) => {
  // Don't allow reordering of default section
  if (result.draggableId === 'default-section') {
    return;
  }

  const [removedSection] = currentSections.splice(result.source.index, 1);
  currentSections.splice(result.destination!.index, 0, removedSection);

  const updatedSections = currentSections.map((section, index) => ({
    ...section,
    sortIndex: index
  }));

  onReorder(currentTiers, updatedSections);
};

const handleTierReorder = (
  result: DropResult,
  currentSections: OutgoingSectionDto[],
  currentTiers: OutgoingEventProductDto[],
  onReorder: (
    tiers: OutgoingEventProductDto[],
    sections: OutgoingSectionDto[]
  ) => void
) => {
  const sourceSectionId = result.source.droppableId;
  const destinationSectionId = result.destination!.droppableId;
  const sourceIndex = result.source.index;
  const destinationIndex = result.destination!.index;

  // Find the tier being moved
  const sourceSectionTiers = currentTiers.filter(
    (t) => (t.sectionId || 'default') === sourceSectionId
  );
  const tierToMove = sourceSectionTiers[sourceIndex];

  // Remove tier from source section
  const tierIndex = currentTiers.findIndex((t) => t.id === tierToMove.id);
  currentTiers.splice(tierIndex, 1);

  // Add tier to destination section
  const destinationSectionTiers = currentTiers.filter(
    (t) => (t.sectionId || 'default') === destinationSectionId
  );
  const insertAtIndex = Math.min(destinationIndex, destinationSectionTiers.length);

  // Find where to insert in the overall tiers array
  const insertBeforeTier = destinationSectionTiers[insertAtIndex];
  const insertPosition = insertBeforeTier
    ? currentTiers.findIndex((t) => t.id === insertBeforeTier.id)
    : currentTiers.length;

  // Insert the tier at the new position with updated sectionId
  const updatedTier = {
    ...tierToMove,
    sectionId: destinationSectionId === 'default' ? undefined : destinationSectionId
  };
  currentTiers.splice(insertPosition, 0, updatedTier);

  // Update sortIndex for all tiers
  const updatedTiers = currentTiers.map((tier, index) => ({
    ...tier,
    sortIndex: index
  }));

  onReorder(updatedTiers, currentSections);
}; 